import { useState, useCallback } from 'react';
import { BranchOfficesState } from '../types/branch-office.types';
import { BranchOfficeService } from '../services/branch-office.service';

const initialState: BranchOfficesState = {
  branchOffices: [],
  isLoading: false,
  error: null,
};

export const useBranchOffices = () => {
  const [state, setState] = useState<BranchOfficesState>(initialState);

  const setLoading = useCallback((isLoading: boolean) => {
    setState(prev => ({ ...prev, isLoading, error: null }));
  }, []);

  const setError = useCallback((error: string) => {
    setState(prev => ({ ...prev, error, isLoading: false }));
  }, []);

  const fetchBranchOffices = useCallback(async () => {
    try {
      setLoading(true);
      const branchOffices = await BranchOfficeService.getBranchOffices();
      setState(prev => ({
        ...prev,
        branchOffices,
        isLoading: false,
        error: null,
      }));
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to fetch branch offices';
      setError(errorMessage);
    }
  }, [setLoading, setError]);

  const fetchBranchOfficesByChain = useCallback(async (chainId: string) => {
    try {
      setLoading(true);
      const branchOffices = await BranchOfficeService.getBranchOfficesByChain(chainId);
      setState(prev => ({
        ...prev,
        branchOffices,
        isLoading: false,
        error: null,
      }));
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to fetch branch offices by chain';
      setError(errorMessage);
    }
  }, [setLoading, setError]);

  const clearError = useCallback(() => {
    setState(prev => ({ ...prev, error: null }));
  }, []);

  const clearBranchOffices = useCallback(() => {
    setState(prev => ({ ...prev, branchOffices: [] }));
  }, []);

  return {
    ...state,
    fetchBranchOffices,
    fetchBranchOfficesByChain,
    clearError,
    clearBranchOffices,
  };
}; 